// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define tooltips.
 *
 * This tool cannot avoid printing "cohesion", "chance level" and "detection
 * floor" — those words ARE the honesty corrections, and replacing them with
 * friendlier ones would mean printing a number without saying what it is. So
 * every one of them is defined a click away instead.
 */

export const GLOSSARY: Record<string, string> = {
  embedding:
    'A list of numbers standing in for a piece of text, arranged so that texts about the same thing end up close together. topicgap looks each word up in a table and averages the rows — there is no neural network running here, which is why 25,000 rows takes seconds.',
  'k-means':
    'An algorithm that sorts points into k groups by repeatedly moving each group\'s centre to the middle of its members. You choose k; it is a setting, not something the data tells you.',
  'spherical k-means':
    'k-means using the angle between vectors rather than the straight-line distance, with each group centre pushed back out onto the unit sphere after every update. It is the variant that matches how these embeddings were trained.',
  'c-tf-idf':
    'Class-based TF-IDF, from BERTopic. It scores each word by how common it is INSIDE one cluster against how common it is across all of them, so the words that come out are the ones that make that cluster different. Pure arithmetic — no language model names anything here.',
  cohesion:
    'The average cosine between a cluster\'s rows and its centre. Higher means tighter. A cluster around 0.9 is one clear topic; one near 0.4 is several topics sharing a bin.',
  ambiguous:
    'The share of a cluster\'s rows that sit almost exactly between two centres. A cluster made largely of these is not a topic, it is a cut through one.',
  'chance level':
    'What an eval set of the same size, drawn at random from your own corpus, would have left uncovered. It is printed beside every coverage number because a number without its chance level looks identical to a finding when it is not one.',
  'detection floor':
    'The cluster size below which your eval set is simply too small to demonstrate a gap. With 40 eval rows a topic under about 7.5% of traffic can be uncovered by luck alone, so anything smaller is unmeasured rather than missing.',
  radius:
    'How far a cluster reaches — the 90th percentile of the distance from its rows to its centre. An eval row only counts as covering a cluster if it lands inside this. Without the gate, an eval set about something else entirely gets smeared over every cluster and reports full coverage.',
  covered:
    'At least one of your eval rows landed inside this cluster\'s radius. Uncovered means your eval set never tested anything in it.',
  centroid: 'The centre of a cluster — the average of its rows, scaled back onto the unit sphere.',
  cosine:
    'A similarity between −1 and 1 measuring the angle between two embeddings. 1 is the same direction, 0 is unrelated. Distance is 1 − cosine, so 0 distance means identical.',
  wordpiece:
    'The way text is chopped into vocabulary entries before lookup. Unknown words are split into pieces that are known, so "carpet" can become "car" + "##pet".',
  'v-measure':
    'The score MTEB uses for clustering: how well the discovered clusters line up with known ground-truth labels. Higher is better, 100 is perfect.',
};

let tip: HTMLElement | null = null;

function ensureTip(): HTMLElement {
  if (tip) return tip;
  tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.hidden = true;
  tip.setAttribute('role', 'tooltip');
  document.body.appendChild(tip);
  return tip;
}

export function hideGlossary(): void {
  if (tip) tip.hidden = true;
}

/** Wire one delegated listener for every `.term` on the page, now and later. */
export function installGlossary(): void {
  const element = ensureTip();

  document.addEventListener('pointerdown', (event) => {
    const target = event.target as HTMLElement;
    const term = target.closest<HTMLElement>('.term');
    if (!term) {
      if (!element.contains(target)) hideGlossary();
      return;
    }
    event.preventDefault();
    const key = (term.dataset.term ?? term.textContent ?? '').toLowerCase().trim();
    const definition = GLOSSARY[key];
    if (!definition) return;

    element.textContent = definition;
    element.hidden = false;
    const box = term.getBoundingClientRect();
    // Measured after unhiding, because a hidden element has no size and the
    // tooltip would be positioned against a zero-width box.
    const width = element.offsetWidth;
    const left = Math.min(Math.max(8, box.left), window.innerWidth - width - 8);
    const below = box.bottom + 8;
    const fitsBelow = below + element.offsetHeight < window.innerHeight - 8;
    element.style.left = `${left}px`;
    element.style.top = fitsBelow ? `${below}px` : `${Math.max(8, box.top - element.offsetHeight - 8)}px`;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideGlossary();
  });
  window.addEventListener('resize', hideGlossary);
  window.addEventListener('scroll', hideGlossary, true);
}

/** Wrap a known term for the tooltip. Unknown terms are returned unchanged. */
export function term(text: string, key = text): string {
  if (!GLOSSARY[key.toLowerCase()]) return text;
  return `<button type="button" class="term" data-term="${key.toLowerCase()}">${text}</button>`;
}
